import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { UserService } from '../user.service';
import { of, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { WcgDialogModule } from '@wcg/frontend-framework';
import { ContactUsDialogComponent } from './contact-us-dialog/contact-us-dialog.component';
import {
  ApiVersionResponse,
  VersionService,
} from '@shared/services/version.service';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth/auth.service';
import { PermissionsService } from '@shared/services/permissions.service';
import {
  PermissionGrants,
  Role,
  ViewOrganizationDetailPermission,
  ViewOrgDetailsPermission,
} from '@shared/interfaces/permissions.interface';
import { catchError, map } from 'rxjs/operators';
import { AuthActions } from '@auth/redux/auth.actions';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatMenuModule,
    MatDividerModule,
    MatIconModule,
    RouterModule,
    WcgDialogModule,
  ],
  selector: 'wcg-headeruser-menu',
  styleUrls: ['./headeruser-menu.component.scss'],
  templateUrl: './headeruser-menu.component.html',
})
export class HeaderuserMenuComponent implements OnInit {
  private readonly _userService = inject(UserService);
  private readonly _store = inject(Store);
  private readonly _dialog = inject(MatDialog);
  private readonly _versionService = inject(VersionService);
  private readonly _authService = inject(AuthService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly isAuthenticated = input<boolean>();
  readonly feVersion = signal<string>('');
  readonly isInternal = signal<boolean>(false);
  readonly isImpersonating = signal<boolean>(false);
  readonly $userInitials = toSignal(this._userService.userInitials$);

  readonly $irisVersion = signal<string>('');
  readonly $apiVersion = signal<string>('');
  readonly $releaseNumber = signal<string>('');
  readonly $isITAdmin = toSignal(
    this._permissionsService.hasRole(Role.ITAdmin).pipe(
      catchError(() => of(false)),
      map((value) => !!value),
    ),
  );

  readonly $organizationId = signal<string>('');

  readonly $canViewOrgDetails = toSignal(
    this._permissionsService.observeUserPermissions(
      [new ViewOrgDetailsPermission(), new ViewOrganizationDetailPermission()],
      [PermissionGrants.CanView],
    ),
    { initialValue: false },
  );

  readonly $canViewMyOrganization = computed(() => {
    const organizationId = this.$organizationId();
    const canViewOrgDetails = this.$canViewOrgDetails();
    return !!organizationId && canViewOrgDetails;
  });

  ngOnInit(): void {
    this._userService
      .getUser()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((user) => {
        if (user) {
          this.isInternal.set(Boolean(user.is_internal));
          this.isImpersonating.set(Boolean(user.is_impersonating));
          this.$organizationId.set(user.organization_id || '');
          this._userService.getUserDetailById(user.id).subscribe();
        } else {
          this.isInternal.set(false);
          this.isImpersonating.set(false);
          this.$organizationId.set('');
        }
      });

    this.feVersion.set(this._versionService.getFeVersion());
    this._versionService
      .getApiVersion()
      .pipe(take(1))
      .subscribe((res: ApiVersionResponse) => {
        this.$apiVersion.set(res.be_build_number || '');
        this.$releaseNumber.set(res.app_version || '');
        this.$irisVersion.set(res.iris_version || '');
      });
  }

  openContactDialog() {
    this._dialog.open(ContactUsDialogComponent, {
      data: { isInternal: this.isInternal() },
    });
  }

  logout(): void {
    // Check if currently impersonating
    this._userService
      .getUser()
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          if (user?.is_impersonating) {
            // If impersonating, dispatch action to end impersonation only
            this._store.dispatch(AuthActions.endImpersonationRequested());
          } else {
            // If not impersonating, dispatch action for full logout
            this._authService.addLogoutInsights('ManualLogout');
            this._store.dispatch(AuthActions.logoutRequested());
          }
        },
      });
  }
}
